/**
 * Node-safe concurrency core for the auto-tuner's evaluation pool.
 *
 * This module deliberately contains NO `Worker` / `?worker` / DOM references so
 * it can be imported by `auto-tuner.ts` (whose `freezeEnv` is exercised by the
 * Node persistence harness) and unit-tested under ts-node. The worker-spawning
 * half lives in `tuner-pool.ts`, imported ONLY by the web entry (`app.ts`) and
 * injected into the tuner via `setAutoTunerPoolFactory`.
 */

import type { Participant, SchedulerConfig, Task } from '../models/types';
import { type EvalResult, type FrozenEnv, runEvaluation } from './tuner-eval';

/** A single evaluation request — the candidate weights + per-phase budget. */
export interface TunerJob {
  config: SchedulerConfig;
  attempts: number;
  maxIterations: number;
  /** Legacy solverRatio-scaled SA wall-clock cap, computed by auto-tuner.mkJob
   *  so each eval matches the pre-pool serial tuner byte-for-byte. */
  maxSolverTimeMs: number;
}

/** The frozen world handed to a pool once; jobs carry only the per-eval delta. */
export interface TunerPoolEnv {
  participants: Participant[];
  tasks: Task[];
  frozen: FrozenEnv;
}

/** Abstract evaluation pool — either a Web Worker pool or the inline fallback. */
export interface TunerPool {
  /** Number of concurrent evaluation lanes. */
  readonly size: number;
  /** Run all jobs (up to `size` at once), firing `onResult` as each lands. */
  runBatch(
    jobs: TunerJob[],
    onResult?: (job: TunerJob, res: EvalResult, index: number) => void,
  ): Promise<EvalResult[]>;
  /** Kill all in-flight work; any pending `runBatch` rejects with `TunerCancelled`. */
  terminate(): void;
}

/** Thrown by a pool's in-flight `runBatch` when `terminate()` is called. */
export class TunerCancelled extends Error {
  constructor() {
    super('Auto-tune cancelled');
    this.name = 'TunerCancelled';
  }
}

/** The result a crashed/uninitialised eval reports — maximally infeasible, so it
 *  is strongly discouraged but never aborts the tournament. Matches the
 *  in-engine catch in `runEvaluation`. */
export const INFEASIBLE_RESULT: EvalResult = { refScore: -1e18, unfilled: 9999, feasible: false, wallMs: 0 };

/**
 * Run `jobs` through `runOne` with at most `concurrency` in flight. Collects
 * results in input order and fires `onResult` as each completes. If a `runOne`
 * REJECTS (the cancellation signal), dispatch stops and the first error is
 * rethrown — but runners catch internally so no unhandled rejection escapes.
 * A `runOne` that RESOLVES with an infeasible result (a crashed worker) does not
 * stop the batch.
 */
export async function runConcurrent<J, R>(
  jobs: J[],
  concurrency: number,
  runOne: (job: J, index: number) => Promise<R>,
  onResult?: (job: J, res: R, index: number) => void,
): Promise<R[]> {
  const results = new Array<R>(jobs.length);
  let next = 0;
  let firstError: unknown = null;
  const cap = Math.max(1, Math.min(concurrency, jobs.length || 1));

  async function runner(): Promise<void> {
    while (true) {
      if (firstError !== null) return;
      const i = next++;
      if (i >= jobs.length) return;
      try {
        const res = await runOne(jobs[i], i);
        results[i] = res;
        onResult?.(jobs[i], res, i);
      } catch (err) {
        if (firstError === null) firstError = err;
        return;
      }
    }
  }

  const runners: Promise<void>[] = [];
  for (let w = 0; w < cap; w++) runners.push(runner());
  await Promise.all(runners); // runners never reject (they catch) ⇒ no unhandled rejection
  if (firstError !== null) throw firstError;
  return results;
}

/**
 * Main-thread fallback pool — used when `Worker` is unavailable (the Node
 * harness, or an exotic runtime). Runs evaluations sequentially via
 * `runEvaluation`; cooperative because `generateScheduleAsync` yields internally.
 */
export function makeInlinePool(env: TunerPoolEnv): TunerPool {
  let canceled = false;
  // Lets terminate() interrupt the CURRENTLY-running eval mid-SA (the engine
  // honors the signal), so cancel on the fallback path is prompt rather than
  // one-eval-latent — matching the Worker pool's instant terminate().
  const abort = new AbortController();
  return {
    size: 1,
    runBatch(jobs, onResult) {
      return runConcurrent(
        jobs,
        1,
        (job) => {
          if (canceled) throw new TunerCancelled();
          return runEvaluation({
            participants: env.participants,
            tasks: env.tasks,
            config: job.config,
            attempts: job.attempts,
            maxIterations: job.maxIterations,
            maxSolverTimeMs: job.maxSolverTimeMs,
            frozen: env.frozen,
            abortSignal: abort.signal,
          });
        },
        onResult,
      );
    },
    terminate() {
      canceled = true;
      abort.abort();
    },
  };
}
